import { AppIcon } from '@/components/ui/app-icon';
import { AuthLayout } from '@/components/auth/auth-layout.component';
import { AuthPreviewCarousel } from '@/components/auth/auth-preview-carousel.component';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/use-theme.hook';
import {
  useCompleteActivationMutation,
  useExchangeCodeMutation,
} from '@/store/features/appsumo.feature';
import {
  SignUp,
  useOrganization,
  useOrganizationList,
  useUser,
} from '@clerk/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { toast } from 'sonner';
import {
  iconBuildings,
  iconCircleCheck,
  iconCircleExclamation,
  iconSpinnerThird,
} from '@/lib/icons';
import { BRAND } from '@/lib/brand';
import { useAppDispatch } from '@/store';
import { billingApi } from '@/store/features/billing.feature';
import { extensionApi } from '@/store/features/extension.feature';
import { waitForAppSumoTier } from '@/utils/wait-for-appsumo-tier.util';

type IconShim = React.ComponentType<{ className?: string }>;
const AlertCircle: IconShim = (p) => (
  <AppIcon icon={iconCircleExclamation} {...p} />
);
const CheckCircle2: IconShim = (p) => <AppIcon icon={iconCircleCheck} {...p} />;
const Loader2: IconShim = (p) => <AppIcon icon={iconSpinnerThird} {...p} />;
const Buildings: IconShim = (p) => <AppIcon icon={iconBuildings} {...p} />;

/**
 * AppSumo OAuth landing page.
 *
 * The flow has three legs:
 *
 *   1. AppSumo redirects the browser here with `?code=<single-use>` —
 *      we POST it to /appsumo/oauth/exchange immediately (the code
 *      expires fast and is single-use). The API returns an opaque
 *      `activation_token` that maps back to the buyer's `license_key`
 *      inside Redis. We persist that token in sessionStorage so it
 *      survives a Clerk signup round-trip.
 *
 *   2. If the user is already signed in, we POST the token to
 *      /appsumo/oauth/complete right away — that's authenticated, so
 *      the API can attach the license to the caller's organization.
 *
 *   3. If they're not signed in, we render Clerk's <SignUp> inline.
 *      `fallbackRedirectUrl` brings them back to this route (with no
 *      `?code=`, since we already burned it), and on remount we see
 *      the activation_token in sessionStorage and finish the bind.
 *
 * Failure modes we surface explicitly:
 *   - exchange failure  → 400 from API → toast + "try again" CTA
 *   - complete failure  → 409 (license already linked to another org)
 *                         or transient → toast + retry button
 *   - activation expired (TTL 15 min) → message + restart link
 */

const ACTIVATION_TOKEN_KEY = 'cleanlist.appsumo.activation_token';

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

export const Route = createFileRoute('/appsumo/activate')({
  component: AppSumoActivate,
  validateSearch: (search: Record<string, unknown>) => ({
    code: asString(search.code),
  }),
});

type Stage =
  | 'exchanging'
  | 'awaiting_signup'
  | 'choosing_org'
  | 'completing'
  | 'done'
  | 'error';

function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(ACTIVATION_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token: string): void {
  try {
    sessionStorage.setItem(ACTIVATION_TOKEN_KEY, token);
  } catch {
    // sessionStorage can throw in private-browsing modes — the page
    // still works, the user just has to re-paste the code if Clerk
    // signup interrupts. Surfacing this would scare more than help.
  }
}

function clearStoredToken(): void {
  try {
    sessionStorage.removeItem(ACTIVATION_TOKEN_KEY);
  } catch {
    // ignore
  }
}

function AppSumoActivate() {
  const { code } = Route.useSearch();
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const {
    isLoaded: isOrgListLoaded,
    userMemberships,
    setActive,
    createOrganization,
  } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // A license goes to a workspace the user owns; the chooser offers only
  // those (the server refuses the rest).
  const memberships = userMemberships?.data ?? [];
  const ownedOrgs = memberships
    .filter((m) => m.role === 'org:admin')
    .map((m) => m.organization);
  // Choose when there are several workspaces, or when the only one isn't
  // theirs (they can create their own from the chooser).
  const needsOrgChoice =
    memberships.length > 1 ||
    (memberships.length === 1 && ownedOrgs.length === 0);
  // Read by the one-shot code exchange, which runs before later renders.
  const needsOrgChoiceRef = useRef(false);
  needsOrgChoiceRef.current = needsOrgChoice;

  const [exchangeCode] = useExchangeCodeMutation();
  const [completeActivation] = useCompleteActivationMutation();
  const dispatch = useAppDispatch();

  // Stage drives the rendered UI. Initial value is exchanging when we
  // have a fresh `?code=`, awaiting_signup if we only have a token
  // from a prior exchange (post-Clerk-signup round-trip).
  const initialStage: Stage = code
    ? 'exchanging'
    : readStoredToken()
      ? 'completing'
      : 'error';

  const [stage, setStage] = useState<Stage>(initialStage);
  const [tier, setTier] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Opened with neither a `?code=` nor a stored token: nothing to verify.
  const [missingCode] = useState(initialStage === 'error');

  // Guard against React 18 StrictMode double-invocation in dev, which
  // would otherwise burn the single-use AppSumo `code` twice.
  const exchangeStartedRef = useRef(false);
  const completeStartedRef = useRef(false);
  // Same guard for the brand-new-signup org bootstrap — Clerk's
  // createOrganization is not idempotent and a double-invocation would
  // produce two empty workspaces.
  const orgBootstrapStartedRef = useRef(false);

  const handleComplete = useCallback(
    async (token: string) => {
      setStage('completing');
      try {
        const res = await completeActivation({
          activation_token: token,
        }).unwrap();
        clearStoredToken();
        setTier(res.tier);

        // Skip the /onboarding flow for AppSumo redeemers: this route
        // already auto-creates a workspace (default name above) and
        // binds the license, so there's nothing for the standard
        // onboarding step machine to collect. Flip
        // ``completed_onboarding`` on the Clerk user so the next
        // OnboardingCheck (on /) doesn't redirect them back into it.
        //
        // user.reload() forces Clerk's SDK to re-fetch so the next
        // useUser() render reads the new flag — without it the
        // OnboardingCheck redirect would race the metadata write and
        // bounce the user to /onboarding for one flash. Mirrors the
        // pattern in ``useCompleteOnboarding`` (use-onboarding.hook.ts).
        //
        // Best-effort: failure here logs + continues. The user will
        // hit /onboarding once and finish it normally — recoverable.
        if (user) {
          try {
            await user.update({
              unsafeMetadata: {
                ...(user.unsafeMetadata ?? {}),
                completed_onboarding: true,
              },
            });
            await user.reload();
          } catch (metaErr) {
            console.warn(
              'AppSumo activate: failed to flip completed_onboarding; user will see /onboarding once',
              metaErr
            );
          }
        }

        // Stay on "Linking your license…" until the tier is applied, then
        // drop any plan/credits read cached before it, so the dashboard
        // doesn't open on the Free plan.
        await waitForAppSumoTier(async () => {
          const access = await dispatch(
            billingApi.endpoints.getFeatureAccess.initiate(
              { organizationId: res.organization_id },
              { forceRefetch: true, subscribe: false }
            )
          ).unwrap();
          return access.appsumo_tier;
        });
        dispatch(
          billingApi.util.invalidateTags([
            'FeatureAccess',
            'BillingProfile',
            'Plans',
          ])
        );
        dispatch(extensionApi.util.invalidateTags(['Credits']));

        setStage('done');
        toast.success(`AppSumo Tier ${res.tier} activated.`);
        // The 1.5s "let them read the success state" pause runs in a
        // useEffect below (keyed on stage === 'done') so that an
        // unmount before the timer fires cancels the navigation
        // instead of routing an unmounted component.
      } catch (err: unknown) {
        clearStoredToken();
        setStage('error');
        const status =
          typeof err === 'object' && err && 'status' in err
            ? (err as { status: number }).status
            : 0;
        if (status === 409) {
          setErrorMessage(
            `This AppSumo license is already linked to a different ${BRAND.name} workspace. Sign in to that workspace, or contact support to transfer it.`
          );
        } else if (status === 400) {
          setErrorMessage(
            'Your activation token expired before we could attach the license. Restart from the AppSumo redemption link in your account.'
          );
        } else {
          setErrorMessage(
            'We could not finish activating your AppSumo license. Please try again in a moment.'
          );
        }
      }
    },
    [completeActivation, dispatch, user]
  );

  // Multi-org chooser: make the picked workspace active, then bind to it.
  const activateOn = useCallback(
    async (organizationId: string) => {
      const stored = readStoredToken();
      if (!stored || completeStartedRef.current) return;
      completeStartedRef.current = true;
      if (organizationId !== organization?.id) {
        try {
          await setActive?.({ organization: organizationId });
        } catch {
          // Keep the token and the chooser: the user can pick again.
          completeStartedRef.current = false;
          toast.error('Could not switch to that workspace. Please try again.');
          return;
        }
      }
      await handleComplete(stored);
    },
    [organization?.id, setActive, handleComplete]
  );

  // No workspace they own: make one (they're its admin), then bind to it.
  const activateOnNewOrg = useCallback(async () => {
    if (!createOrganization || completeStartedRef.current) return;
    try {
      const org = await createOrganization({
        name: user?.firstName
          ? `${user.firstName}'s Workspace`
          : 'My Workspace',
      });
      await activateOn(org.id);
    } catch {
      toast.error('Could not create a workspace. Please try again.');
    }
  }, [createOrganization, user?.firstName, activateOn]);

  // Leg 1: burn the `?code=` for an activation_token.
  useEffect(() => {
    if (!code || exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    (async () => {
      try {
        const res = await exchangeCode({ code }).unwrap();
        writeStoredToken(res.activation_token);
        setTier(res.license_tier);
        // Drop the `?code=` from the URL so a refresh doesn't try to
        // burn it again (it's already single-used at this point).
        // Use TanStack Router's navigate with replace=true so the
        // router's internal state stays in sync — a direct
        // window.history.replaceState would bypass the router and
        // leave validateSearch's `code` reading stale.
        navigate({
          to: '/appsumo/activate',
          search: { code: undefined },
          replace: true,
        });
        if (isLoaded && isSignedIn && needsOrgChoiceRef.current) {
          // Multi-org: the stored token waits for the workspace choice.
          setStage('choosing_org');
        } else if (isLoaded && isSignedIn) {
          // Claim the bind before the navigate above drops `?code=`, or
          // Leg 3 sees the stored token and calls /complete a second time.
          completeStartedRef.current = true;
          await handleComplete(res.activation_token);
        } else {
          setStage('awaiting_signup');
        }
      } catch (err: unknown) {
        setStage('error');
        const status =
          typeof err === 'object' && err && 'status' in err
            ? (err as { status: number }).status
            : 0;
        if (status === 503) {
          setErrorMessage(
            'AppSumo activation is not configured on our side yet. Please contact support.'
          );
        } else {
          setErrorMessage(
            'We could not redeem your AppSumo activation code. It may have already been used — try the redemption link in your AppSumo account again.'
          );
        }
      }
    })();
  }, [code, exchangeCode, handleComplete, isLoaded, isSignedIn, navigate]);

  // Leg 3: we arrived here with no `?code=` but a stored token —
  // happens right after Clerk signup redirects back here.
  //
  // The bind step (/oauth/complete) is org-scoped, so we MUST have an
  // active organization in the Clerk session before calling it. A
  // brand-new signup lands here with zero memberships (Clerk only auto-
  // creates orgs when the SignUp widget includes a workspace step,
  // which ours doesn't), and without this bootstrap the backend's
  // `_get_user_organization_id` 401s and the buyer sees the generic
  // "Activation could not be completed" error — they end up with a
  // Clerk account but no license attached, which is exactly the
  // registration-flow failure mode reported 2026-05-12.
  useEffect(() => {
    if (code) return;
    if (!isLoaded || !isSignedIn) return;
    if (!isOrgLoaded || !isOrgListLoaded) return;
    if (completeStartedRef.current) return;

    const stored = readStoredToken();
    if (!stored) return;

    if (organization) {
      // Multi-org: a lifetime license lifts ONE workspace, so a user in
      // several says which rather than it landing on whichever is active.
      if (needsOrgChoice) {
        setStage('choosing_org');
        return;
      }
      // Happy path: an active org is already in the session — either
      // the user was already signed in pre-flow, or Clerk's setActive
      // from a prior tick has caught up. Proceed straight to /complete.
      completeStartedRef.current = true;
      void handleComplete(stored);
      return;
    }

    const memberships = userMemberships?.data ?? [];
    if (memberships.length > 0 && setActive) {
      // The user has memberships but none is active (e.g. Clerk's
      // session JWT is mid-refresh after signup). Activate the first
      // one — useOrganization() will re-render with `organization`
      // populated and the effect runs again to call /complete.
      const target = memberships[0]?.organization;
      if (target?.id) {
        void setActive({ organization: target.id });
      }
      return;
    }

    if (!createOrganization || orgBootstrapStartedRef.current) return;
    orgBootstrapStartedRef.current = true;

    // No org at all — synthesize one so /complete has something to
    // bind the license to. Name from the user's profile when possible;
    // they can rename it in Settings → Team afterward.
    const defaultOrgName =
      (user?.firstName && `${user.firstName}'s Workspace`) ||
      (user?.primaryEmailAddress?.emailAddress &&
        `${user.primaryEmailAddress.emailAddress.split('@')[0]}'s Workspace`) ||
      'My Workspace';

    void (async () => {
      try {
        const created = await createOrganization({ name: defaultOrgName });
        if (created?.id && setActive) {
          await setActive({ organization: created.id });
          // Don't call /complete here — `setActive` triggers a Clerk
          // session refresh and a re-render; the next pass through
          // this effect (with `organization` now populated) will run
          // the bind. Calling immediately would race the JWT refresh.
        } else {
          throw new Error('Workspace creation returned no id');
        }
      } catch (err) {
        orgBootstrapStartedRef.current = false;
        clearStoredToken();
        setStage('error');
        setErrorMessage(
          err instanceof Error && err.message
            ? `We could not finish setting up your workspace: ${err.message}`
            : 'We could not finish setting up your workspace before linking your AppSumo license. Please refresh and try again.'
        );
      }
    })();
  }, [
    needsOrgChoice,
    code,
    isLoaded,
    isSignedIn,
    isOrgLoaded,
    isOrgListLoaded,
    organization,
    userMemberships,
    setActive,
    createOrganization,
    user,
    handleComplete,
  ]);

  // After a successful activation, drop the user at the dashboard
  // following a short success-state pause. Effect-scoped so unmount
  // (e.g. the user clicks "Go to dashboard" themselves) clears the
  // pending timer rather than navigating an unmounted component.
  useEffect(() => {
    if (stage !== 'done') return;
    const handle = window.setTimeout(() => {
      navigate({ to: '/' });
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [stage, navigate]);

  return (
    <AuthLayout
      leftPanel={
        <>
          <div className='mb-8 text-center'>
            <h1 className='text-2xl font-semibold text-foreground'>
              Activate your AppSumo deal
            </h1>
            {stage !== 'error' && (
              <p className='mt-2 text-sm text-tertiary-foreground'>
                {tier !== null
                  ? `Tier ${tier} — finish setting up your workspace below.`
                  : 'Hang tight while we verify your purchase.'}
              </p>
            )}
          </div>

          {(stage === 'exchanging' || stage === 'completing') && (
            <StatusCard
              icon={<Loader2 className='h-8 w-8 animate-spin text-[#3E8BFF]' />}
              title={
                stage === 'exchanging'
                  ? 'Verifying your AppSumo license…'
                  : 'Linking your license to your workspace…'
              }
              body='This usually takes a second or two.'
            />
          )}

          {stage === 'awaiting_signup' && (
            <SignUp
              fallbackRedirectUrl='/appsumo/activate'
              signInUrl='/auth/login'
              appearance={{
                elements: {
                  rootBox: 'mx-auto',
                  cardBox:
                    'bg-white dark:bg-[#1a1a1a] shadow-xs dark:shadow-none border border-gray-100 dark:border-white/6',
                  card: 'bg-white dark:bg-[#1a1a1a] shadow-none border-0',
                  headerTitle: 'text-gray-900 dark:text-white',
                  headerSubtitle: 'text-gray-500 dark:text-gray-400',
                  socialButtonsBlockButton:
                    'bg-white border-gray-100 hover:bg-gray-50 text-gray-700 dark:bg-[#1a1a1a] dark:border-white/6 dark:hover:bg-white/[0.03] dark:text-gray-300',
                  formFieldLabel: 'text-gray-700 dark:text-gray-300',
                  formFieldInput:
                    'bg-white border-gray-100 text-gray-900 placeholder:text-gray-400 dark:bg-[#1a1a1a] dark:border-white/6 dark:text-white dark:placeholder:text-gray-500',
                  formButtonPrimary:
                    'bg-primary text-primary-foreground hover:bg-primary/90',
                  footer:
                    'bg-white text-gray-500 dark:bg-[#1a1a1a] dark:text-gray-400',
                  footerAction: 'bg-white dark:bg-[#1a1a1a]',
                  footerActionLink: 'text-primary hover:text-primary/80',
                  footerActionText: 'text-gray-500 dark:text-gray-400',
                },
                variables: {
                  colorPrimary: '#3E8BFF',
                  colorBackground: isDark ? '#1a1a1a' : '#ffffff',
                  colorForeground: isDark ? '#ffffff' : '#111827',
                  colorMutedForeground: isDark ? '#9ca3af' : '#6b7280',
                },
              }}
            />
          )}

          {stage === 'choosing_org' && (
            <StatusCard
              icon={<Buildings className='h-8 w-8 text-[#3E8BFF]' />}
              title='Which workspace gets this license?'
              body={
                ownedOrgs.length
                  ? 'An AppSumo license upgrades one workspace you own. Pick the one to activate it on.'
                  : "You're not an admin of any of your workspaces. Create your own to activate this license on, or ask a workspace admin to redeem it."
              }
              cta={
                <div className='mt-4 flex flex-col gap-2'>
                  {ownedOrgs.map((org) => (
                    <Button
                      key={org.id}
                      size='sm'
                      variant={
                        org.id === organization?.id ? 'default' : 'outline'
                      }
                      onClick={() => void activateOn(org.id)}
                    >
                      {org.name}
                    </Button>
                  ))}
                  {ownedOrgs.length ? null : (
                    <Button size='sm' onClick={() => void activateOnNewOrg()}>
                      Create my workspace
                    </Button>
                  )}
                </div>
              }
            />
          )}

          {stage === 'done' && (
            <StatusCard
              icon={<CheckCircle2 className='h-8 w-8 text-emerald-500' />}
              title={`AppSumo Tier ${tier} activated`}
              body='Taking you to your workspace…'
              cta={
                <Button
                  size='sm'
                  className='mt-4'
                  onClick={() => navigate({ to: '/' })}
                >
                  Go to dashboard
                </Button>
              }
            />
          )}

          {stage === 'error' && (
            <StatusCard
              icon={<AlertCircle className='h-8 w-8 text-amber-500' />}
              title={
                missingCode
                  ? 'This activation link is missing its code'
                  : 'Activation could not be completed'
              }
              body={
                missingCode
                  ? 'Open the redemption link in your AppSumo account again. If it keeps happening, email support@cleanlist.ai.'
                  : (errorMessage ??
                    'Something went wrong while activating your AppSumo license.')
              }
              cta={
                <Button
                  variant='outline'
                  size='sm'
                  className='mt-4'
                  onClick={() => navigate({ to: '/' })}
                >
                  Back to {BRAND.name}
                </Button>
              }
            />
          )}
        </>
      }
      rightPanel={<AuthPreviewCarousel variant='appsumo' />}
    />
  );
}

interface StatusCardProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  cta?: React.ReactNode;
}

function StatusCard({ icon, title, body, cta }: StatusCardProps) {
  return (
    <div className='rounded-lg border border-slate-200 bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900'>
      <div className='mx-auto mb-4 flex h-12 w-12 items-center justify-center'>
        {icon}
      </div>
      <h2 className='mb-2 text-lg font-semibold text-foreground'>{title}</h2>
      <p className='text-sm text-muted-foreground'>{body}</p>
      {cta}
    </div>
  );
}

export default AppSumoActivate;
